import { Locale, SourceKind } from 'types/schema-generated-types';

export interface CommonConfigOptions {
  hiddenRoutes: string[];
  blacklistedBreadcrumbsPathParams: string[];
  contactInformation: { title: string; values: string[] }[];
  contactTopics: { name: string; tag?: string }[];
  locales: { label: string; value: string }[];
  privilegesGroups: {
    moduleLevel: {
      adminConsole: {
        any: string[];
        userManagement: string[];
        productsLicenses: string[];
        classesStudents: string[];
        organizations: string[];
        rosterManagement: string[];
        administratorHelp: string[];
      };
    };
    assignClassroomLicense: string[];
    assignOrgLicense: string[];
    createClassroom: string[];
    mutateClassroom: string[];
    createStudent: string[];
    mutateStudent: string[];
    mutateOrganization: string[];
    createUser: string[];
    mutateUser: string[];
    mutateAcademicSession: string[];
    updateClassroomLicense: string[];
    updateOrgLicense: string[];
  };
  validExternalIdSourceKinds: SourceKind[];
  endOfYear: {
    notificationDays: number;
    minAdvanceDays: number;
    limitDay: string;
  };
  reporting: {
    asyncPollIntervalMs: number;
  };
  combinedProductFamilyTiles: { productFamilyCodes: string[]; name: string }[];
  appAuditorPrivilegeMap: Map<string, Set<string>>;
  appTileOverrides: { app: string; appToSuppress: string }[];
}

const blacklistedBreadcrumbsPathParams = ['modalView'];
const hiddenRoutes: string[] = [];
const contactInformation = [
  {
    title: 'Office Hours',
    values: ['Monday - Friday', '8:30 am - 5:30 pm (ET)']
  },
  {
    title: 'August & September Hours',
    values: ['Monday - Thursday', '8:30 am - 7:30 pm (ET)', 'Friday', '8:30 am - 5:30 pm (ET)']
  },
  {
    title: 'Phone',
    values: ['301.263.2700', '1.888.983.8409']
  },
  {
    title: 'Email',
    values: ['technology@lwtears.com']
  }
];

const contactTopics = [
  { name: 'Getting Started – Account Management' },
  { name: 'Getting Started - User Management' },
  { name: 'Getting Started - Class Management' },
  { name: 'Getting Started - Student Management' },
  { name: 'District/School – Roster Management for New School Year' },
  { name: '+Live Insights Support' },
  { name: 'KWT Program Assistance' },
  { name: 'GSS (PreK) Program Assistance' },
  { name: 'HWT Program Assistance' },
  { name: 'Wet-Dry-Try Program Assistance' },
  { name: 'Pricing Information' }
];

const privilegesGroups = {
  moduleLevel: {
    adminConsole: {
      // TODO: could probably build a pattern-matcher to reduce duplication
      any: [
        'admin-console.user-management.read.org',
        'admin-console.products-licenses.read.org',
        'admin-console.classes-students.read.org',
        'admin-console.organizations.read.org',
        'admin-console.roster-management.read.org',
        'admin-console.administrator-help.read.org'
      ],
      userManagement: ['admin-console.user-management.read.org'],
      productsLicenses: ['admin-console.products-licenses.read.org'],
      classesStudents: ['admin-console.classes-students.read.org'],
      organizations: ['admin-console.organizations.read.org'],
      rosterManagement: ['admin-console.roster-management.read.org'],
      administratorHelp: ['admin-console.administrator-help.read.org']
    }
  },
  assignClassroomLicense: [
    'azsapp-license.assign.classroom',
    'gss-license.assign.classroom',
    'hwt-license.assign.classroom',
    'keyboarding-license.assign.classroom',
    'prm-license.assign.classroom',
    'wdt-license.assign.classroom'
  ],
  assignOrgLicense: [
    'azsapp-license.assign.org',
    'aztapp-license.assign.org',
    'bwc-license.assign.org',
    'gss-license.assign.org',
    'hwt-license.assign.org',
    'idtt-license.assign.org',
    'keyboarding-license.assign.org',
    'prekitt-license.assign.org',
    'prm-license.assign.org',
    'wdt-license.assign.org'
  ],
  createClassroom: ['classroom.create.org'],
  mutateClassroom: ['classroom.write.classroom', 'classroom.write.org'],
  createStudent: ['student.create.classroom', 'student.create.org'],
  mutateStudent: ['student.write.classroom', 'student.write.org'],
  mutateOrganization: ['organization.write.org'],
  createUser: ['user.create.org'],
  mutateUser: ['user.write.org'],
  mutateAcademicSession: ['academic-session.write.org'],
  updateClassroomLicense: [
    'azsapp-license.update-curriculum.classroom',
    'gss-license.update-curriculum.classroom',
    'hwt-license.update-curriculum.classroom',
    'keyboarding-license.update-curriculum.classroom',
    'prm-license.update-curriculum.classroom',
    'wdt-license.update-curriculum.classroom'
  ],
  updateOrgLicense: [
    'azsapp-license.update-curriculum.org',
    'aztapp-license.update-curriculum.org',
    'bwc-license.update-curriculum.org',
    'gss-license.update-curriculum.org',
    'hwt-license.update-curriculum.org',
    'idtt-license.update-curriculum.org',
    'keyboarding-license.update-curriculum.org',
    'prekitt-license.update-curriculum.org',
    'prm-license.update-curriculum.org',
    'wdt-license.update-curriculum.org'
  ]
};

const locales = [
  { label: 'English', value: Locale.EN_US },
  { label: 'Español', value: Locale.ES_US }
];

const validExternalIdSourceKinds = [SourceKind.GOOGLE];

const endOfYear = {
  notificationDays: 90,
  minAdvanceDays: 5,
  limitDay: '12/31'
};

const reporting = {
  asyncPollIntervalMs: 3000
};

const combinedProductFamilyTiles = [
  { productFamilyCodes: ['GSS', 'PREKITT'], name: 'Get Set for School' }
];

const appAuditorPrivilegeMap = new Map<string, Set<string>>([
  ['bwc-educator', new Set(['idtt-bwc-application.auditor-access.org'])],
  [
    'gss-educator-26',
    new Set([
      'idtt-gss-ll-application.auditor-access.org',
      'idtt-gss-rw-application.auditor-access.org'
    ])
  ],
  ['hwt-educator-22', new Set(['idtt-22-application.auditor-access.org'])],
  ['hwt-educator-25', new Set(['idtt-25-application.auditor-access.org'])],
  ['prm-educator', new Set(['prm-application.auditor-access.org'])]
]);

const appTileOverrides = [{ app: 'hwt-educator-25', appToSuppress: 'hwt-educator-22' }];

const CommonConfig: CommonConfigOptions = {
  hiddenRoutes,
  blacklistedBreadcrumbsPathParams,
  contactInformation,
  contactTopics,
  privilegesGroups,
  locales,
  validExternalIdSourceKinds,
  endOfYear,
  reporting,
  combinedProductFamilyTiles,
  appAuditorPrivilegeMap,
  appTileOverrides
};

export default CommonConfig;
